/**
 * Abstract class used as a SliceView item source for rendering Protein and Sequence linear maps.
 * Operates on a single polymer, so not appropriate for use in alignment maps.
 */

// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS104: Avoid inline assignments
 * DS206: Consider reworking classes to avoid initClass
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import * as d3 from 'd3';
import _ from 'underscore';

import {ComponentsSequence} from 'components/Sequence';
import {SliceComponentName, SliceMapType} from 'constants/MapsConstants';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import * as infinitescroller from 'molbio/maps/linear/InfiniteScroller';
import {AminoAcids, DNA} from 'molbio/maps/linear/LinearLayout';
import Slice, {SliceAttributes} from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import {PolymerLinearMapState} from 'molbio/maps/MapStates';
import {foreignObjectRenderingEnabled} from 'molbio/maps/SequenceMapConstants';
import Type from 'molbio/maps/Type';
import SinglePolymerMapTabState from 'molbio/tabstates/SinglePolymerMapTabState';

const SIDEPADDING = 15;
const SCROLLBAR_WIDTH = 10;

interface MergedLayoutInterface {
  get(attribute: 'type'): Type;
}

export interface PolymerSliceItemSourceAttributes<
  Polymer extends ComponentsSequence | AaSequence,
  MapState extends PolymerLinearMapState<Polymer>,
  TabState extends SinglePolymerMapTabState<Polymer, MapState>,
> extends infinitescroller.ItemSourceAttributes<SliceView> {
  mapstate: MapState;
  tabstate: TabState;
  scroller: infinitescroller.InfiniteScroller<SliceView>;
  $linear: JQuery;
}

interface PolymerSliceItemSource<
  Polymer extends ComponentsSequence | AaSequence,
  MapState extends PolymerLinearMapState<Polymer>,
  TabState extends SinglePolymerMapTabState<Polymer, MapState>,
> {
  get<K extends keyof PolymerSliceItemSourceAttributes<Polymer, MapState, TabState>>(
    key: K
  ): PolymerSliceItemSourceAttributes<Polymer, MapState, TabState>[K];
  set<K extends keyof PolymerSliceItemSourceAttributes<Polymer, MapState, TabState>>(
    attributeName: K,
    value: PolymerSliceItemSourceAttributes<Polymer, MapState, TabState>[K],
    options?: Backbone.ModelSetOptions
  ): PolymerSliceItemSource<Polymer, MapState, TabState>;
  set(
    obj: Partial<PolymerSliceItemSourceAttributes<Polymer, MapState, TabState>>
  ): PolymerSliceItemSource<Polymer, MapState, TabState>;
}

// Used to render both Sequence and Proteins
abstract class PolymerSliceItemSource<
  Polymer extends ComponentsSequence | AaSequence,
  MapState extends PolymerLinearMapState<Polymer>,
  TabState extends SinglePolymerMapTabState<Polymer, MapState>,
> extends infinitescroller.ItemSource<SliceView> {
  mapstate!: MapState;
  tabstate!: TabState;
  $linear!: JQuery;

  override initialize(options: PolymerSliceItemSourceAttributes<Polymer, MapState, TabState>): void {
    // oxlint-disable-next-line prefer-rest-params
    super.initialize(...arguments);
    this.mapstate = options.mapstate;
    this.tabstate = options.tabstate;
    this.$linear = options.$linear;
    this.calcMaxBases();
    this.updateLength();
    this.listenTo(this.tabstate.get('bufferViewLayout'), 'change:height', () => {
      this.trigger('resizeY');
    });
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.tabstate.get('bufferViewLayout'), 'change:width', this.calcMaxBases);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate, 'change:zoom', this.calcMaxBases);
    this.listenTo(this.mapstate, 'change:maxBases', () => {
      this.updateLength();
    });
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate, 'change:maxBases', this.resizeXHandler);
  }

  getResiduesLayout(): Polymer extends ComponentsSequence ? DNA : AminoAcids {
    return this.mapstate.getResiduesLayout();
  }

  calcMaxBases(): number {
    const wrapperWidth = this.tabstate.get('bufferViewLayout')!.get('width');
    const layout: MergedLayoutInterface = this.getResiduesLayout();
    const type = layout.get('type');
    // Some tabstates need extra room at the end of the slice to show features that are between bases
    const adjustment = this.tabstate.BASES_PER_SLICE_ADJUSTMENT ?? 0;

    // The +PADDING/2 lets it sometimes cut into the padding and sometimes be under the padding,
    // depending on how characters end up being rounded, instead of always being under.
    let maxBases = /Firefox/i.test(navigator.userAgent)
      ? type.numChars(wrapperWidth - 2 * SIDEPADDING - SCROLLBAR_WIDTH)
      : type.numChars(wrapperWidth - 2 * SIDEPADDING - SCROLLBAR_WIDTH + SIDEPADDING / 2);
    maxBases += adjustment;

    const isValidMaxBases = maxBases > 0;
    if (!isValidMaxBases) {
      // This seems to frequently happen on mobile browsers; there is likely not enough space to render
      // _any_ bases in given element width, so skip rendering. Use an arbitrary default to avoid downstream errors.
      maxBases = 80;
    }
    this.mapstate.set({
      maxBases,
      skipRender: !isValidMaxBases,
    });
    return maxBases;
  }

  resizeXHandler(): void {
    let endSlice: number;
    const selection = this.mapstate.polymer().selection();
    const startBP = this.get('scroller').firstRendered()!.item().slice.get('start')!;
    const targetSlice = Math.floor(startBP / this.mapstate.get('maxBases'));

    // If the selection was visible, try to keep it visible.
    const startSlice = Math.floor(selection.start() / this.mapstate.get('maxBases'));
    if (selection.isNotEmpty()) {
      endSlice = Math.floor(selection.end() / this.mapstate.get('maxBases'));
    } else {
      endSlice = startSlice;
    }

    // Was it previously visible?
    const wasVisible = _.any(this.get('scroller').renderedItems, (item) => {
      let middle;
      // oxlint-disable-next-line no-return-assign
      return startSlice <= (middle = item.index()) && middle <= endSlice;
    });

    // Signal to the InfiniteScroller that it needs to handle resize.
    this.trigger('resizeX', wasVisible ? startSlice : targetSlice);
  }

  sliceData(index: number): Partial<SliceAttributes> {
    const layout: MergedLayoutInterface = this.getResiduesLayout();
    const max = this.mapstate.get('maxBases');
    const length = this.mapstate.polymer().length();

    const start = max * index;
    const end = Math.min(start + max, length);

    const width = layout.get('type').width(end - start) + layout.get('type').letterSpacing;
    const x = d3.scaleLinear().clamp(true).domain([start, end]).range([0, width]);
    const xInv = d3.scaleLinear().clamp(true).domain([0, width]).range([start, end]);

    const mapWidth = this.tabstate.get('bufferViewLayout')!.get('width');

    // eslint-disable-next-line no-restricted-syntax
    return _.extend(
      {
        start,
        end,
        width,
        x,
        xInv,
        maxWidth: mapWidth,
        visibleWidth: mapWidth,
        // Avoid using a default height of 100 as there are scenarios where the actual computed slice
        // height is 100, which prevents SliceView.triggerHeightChange() from causing the InfiniteScroller
        // from rendering multiple slices (BNCH-2083)
        height: 100.1,
        y: 0,
      },
      this.extraSliceData(index)
    );
  }

  extraSliceData(index: number): Partial<SliceAttributes> {
    return {};
  }

  indexOf(base: number): number {
    const max = this.mapstate.get('maxBases');
    const index = Math.floor(base / max);
    return index;
  }

  update(): void {
    // Update length
    this.updateLength();
    // Rerender all visible slices.
    // eslint-disable-next-line no-restricted-syntax
    _.each(this.get('scroller').renderedItems, (item) => {
      const sliceView = item.item();
      this.updateItem(sliceView, item.index());
    });
  }

  override updateItem(sliceView: SliceView, index: number): void {
    const sliceData = this.sliceData(index);
    sliceView.slice.set(sliceData, {skipUpdate: true}); // avoid double render: we update() on the next line
    sliceView.update();
  }

  getItem(index: number): SliceView {
    const mapType = SliceMapType.linear;
    const slice = new Slice(this.sliceData(index));

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const sliceView = new SliceView({
      slice,
      selection: this.mapstate.polymer().selection(),
      model: this.mapstate,
      tabstate: this.tabstate,
      mapType,
      el: svg,
      focusableParent: this.$linear,
      y: this.sliceViewY(),
      z: this.sliceViewZ(),
      svgSupportsForeignObject: foreignObjectRenderingEnabled(),
    });
    d3.select(svg).classed(sliceView.className, true);
    return sliceView;
  }

  updateLength(): void {
    this.set('length', Math.ceil(this.mapstate.polymer().length() / this.mapstate.get('maxBases')));
  }

  setScroller(scroller: infinitescroller.InfiniteScroller<SliceView>): void {
    this.set('scroller', scroller);
  }

  // The y components to pass into the SliceView.
  abstract sliceViewY(): Array<SliceComponentName>;

  // The z components to pass into the SliceView.
  abstract sliceViewZ(): Array<SliceComponentName>;
}

export default PolymerSliceItemSource;
